import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface DrawerDetalheProps {
  aberto: boolean;
  onFechar: () => void;
  /**
   * Cor da faixa de 4px no alto, em classe de fundo.
   *
   * É a MESMA da espinha da linha de onde se veio. Sem ela, o drawer é um
   * painel que aparece do nada; com ela, é a continuação visível daquela
   * linha — e é por isso que a faixa existe.
   */
  faixa?: string;
  titulo: string;
  numero: string;
  /** Abre os autos. Ausente: o número fica como texto, não como link. */
  onAbrirAutos?: () => void;
  partes?: string | null;
  /** Os selos logo abaixo do número: tipo, procedência, situação. */
  selos?: ReactNode;
  /**
   * O corpo, em seções empilhadas em coluna. Uma seção com `flex-1` ocupa o
   * que sobra e rola por dentro; as outras ficam. Se a tela for baixa demais
   * para todas, o corpo inteiro rola.
   */
  children: ReactNode;
  /** O rodapé: duas ações e, quando houver, um “mais”. */
  acoes: ReactNode;
  /** Para o leitor de tela: “Dados do processo”, “A comunicação”. */
  rotulo: string;
}

/**
 * O detalhe em drawer (porte do branch do Rubens, `1c5764f3`). A lista é para
 * varrer; o detalhe é um documento, que se lê. A anatomia:
 *
 *   faixa de 4px   a cor da urgência, continuada da linha
 *   cabeça FIXA    classe, número que abre os autos, partes, selos
 *   corpo ROLANTE  seções separadas por fio, sem caixas aninhadas
 *   rodapé FIXO    as ações
 *
 * 560px e não 480: o texto que se lê aqui é o conteúdo principal, e a 480
 * cabiam 58 caracteres por linha — abaixo da medida em que se lê um parágrafo
 * sem perder a linha seguinte. A 560 cabem 70.
 *
 * Fecha pelo X (que o `SheetContent` já traz, pregado no alto), por Esc ou
 * por clique no véu.
 */
export function DrawerDetalhe({
  aberto,
  onFechar,
  faixa = 'bg-border',
  titulo,
  numero,
  onAbrirAutos,
  partes,
  selos,
  children,
  acoes,
  rotulo,
}: DrawerDetalheProps) {
  return (
    <Sheet open={aberto} onOpenChange={(open) => !open && onFechar()}>
      <SheetContent
        side="right"
        aria-label={rotulo}
        // Sem partes não há SheetDescription; sem isto o Radix avisa no console.
        {...(partes ? {} : { 'aria-describedby': undefined })}
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[560px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <span className={cn('h-1 flex-none', faixa)} aria-hidden="true" />

        {/* A CABEÇA NÃO ROLA. Um texto de intimação tem oito mil caracteres;
            rolando até o fim deles, sem isto, não há mais nada na tela que
            diga de que processo se está falando. */}
        <div className="flex-none border-b border-border bg-muted/40 pb-4 pl-6 pr-14 pt-5 sm:pr-12">
          <SheetTitle className="break-words text-lg font-bold leading-snug tracking-tight">
            {titulo}
          </SheetTitle>

          <div className="mt-1.5 flex items-center gap-1">
            {onAbrirAutos ? (
              <Button
                type="button"
                variant="unstyled"
                onClick={onAbrirAutos}
                aria-label={`Abrir os autos do processo ${numero}`}
                className="inline-flex h-auto min-h-11 items-center gap-1.5 rounded p-0 font-mono text-sm font-semibold tracking-tight text-primary underline-offset-2 hover:underline sm:min-h-0"
              >
                {numero}
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
                </svg>
              </Button>
            ) : (
              <span className="font-mono text-sm font-semibold tracking-tight">{numero}</span>
            )}
            {numero && <CopyButton text={numero} label="Copiar número do processo" />}
          </div>

          {partes && (
            <SheetDescription className="mt-2 break-words text-sm leading-relaxed">
              {partes}
            </SheetDescription>
          )}

          {selos && <div className="mt-3 flex flex-wrap gap-1.5">{selos}</div>}
        </div>

        {/* O CORPO. Fica entre a cabeça e o rodapé; rola inteiro só quando a
            tela não comporta as seções. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>

        {/* O RODAPÉ FICA. Age-se depois de ler, e a ação não deve custar uma
            rolagem de volta ao topo. */}
        <div className="flex flex-none gap-2 border-t border-border bg-muted/40 px-6 py-3.5">
          {acoes}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Uma seção do corpo: fio embaixo, respiro igual, e nada de caixa dentro de
 * caixa. O título é a pergunta que a seção responde, em caixa normal — um
 * rótulo em versalete gritado acima de cada bloco é moldura, não informação.
 */
export function SecaoDrawer({
  titulo,
  children,
  ultima = false,
  acaoNoTitulo,
  className,
}: {
  titulo: string;
  children: ReactNode;
  /** Sem o fio de baixo: é a última da pilha. */
  ultima?: boolean;
  /** Botões à direita do título (copiar, imprimir). */
  acaoNoTitulo?: ReactNode;
  /** `flex-1 …` para a seção que ocupa o espaço que sobra e rola por dentro. */
  className?: string;
}) {
  return (
    <section className={cn('flex-none px-6 py-4', !ultima && 'border-b border-border', className)}>
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold text-muted-foreground">{titulo}</h3>
        {acaoNoTitulo && <div className="flex flex-none items-center gap-0.5">{acaoNoTitulo}</div>}
      </div>
      {children}
    </section>
  );
}

/**
 * A caixa dourada do que há para fazer.
 *
 * Mesmo lugar e mesma forma nas duas abas: é a única coisa no drawer que não
 * é consulta, e sim tarefa. A ressalva sobre a contagem vem junto, sempre —
 * o sistema lê o prazo no texto, não o calcula, e omitir isso seria deixar
 * uma conta por fazer parecer uma conta feita.
 */
export function OQueHaParaFazer({
  frase,
  origem,
  ressalva,
}: {
  frase: string;
  origem?: string;
  ressalva: string;
}) {
  return (
    <>
      <div className="rounded-lg border border-[var(--color-gold-border)] bg-[var(--color-gold-soft)] px-3.5 py-3">
        <p className="break-words text-base font-semibold leading-snug">{frase}</p>
        {origem && <p className="mt-1.5 text-xs leading-relaxed text-[var(--color-gold)]">{origem}</p>}
      </div>
      <p className="mt-2.5 text-xs leading-relaxed text-muted-foreground">{ressalva}</p>
    </>
  );
}
